import { Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import classNames from 'classnames';

import { useTranslate } from "../../../../../i18n/useTranslationModules";
import { Survey, PageModule, SurveyQuestion, SurveyQuestionOption, SurveyAnswerBody, SurveyType } from "../../../../../types/working-model";
import { TypographyItem } from "../../../../general-ui/typography-item/typography-item";
import SurveyOption from "./survey-option";
import { getStylingOverrides } from "../../../utils";
import { ParamsProps } from "../../../live-event";
import { useAppDispatch, useTypedSelector } from "../../../../../store/reducers/use-typed-selector";
import { SubmitSurveyAnswer } from "../../../../../connection/save-survey-answer";
import { GetSurveyQuestionResults } from "../../../../../connection/surveys";
import { getStorageObject, setStorageObject } from "../../../../../utils/local-storage";
import { getPollAnswerLocalStorageKey, getPollAnswerLocalStorageKeyBackwardsCompatible } from "./survey";
import useTranslationRoute from "../../../hooks/use-translation-route";
import WaitingIndicator from "../../../../general-ui/waiting-indicator/waiting-indicator";
import { checkQuizzesForCompletion } from "../../../../../store/actions/event/event-actions";

interface IRadioSurveyQuestionProps {
	question: SurveyQuestion;
	module: PageModule;
	template: string;
	sessionUuid?: string;
	survey: Survey;
	setCurrentQuestion: Dispatch<SetStateAction<number>>;
	currentQuestion: number;
	maxQuestions: number;
	viewAll: boolean;
	setShowQuizCompleteScreen?: Dispatch<SetStateAction<boolean>>;
}

const RadioSurveyQuestion: React.FC<IRadioSurveyQuestionProps> = ({
	survey,
	question,
	module,
	template,
	sessionUuid,
	setCurrentQuestion,
	currentQuestion,
	maxQuestions,
	viewAll,
	setShowQuizCompleteScreen
}) => {
	const { show_results, survey: surveyId } = survey;
	const { content, styling_overrides } = module;
	const stylingOverrides = getStylingOverrides(styling_overrides);
	const {
		primaryButtonOverrides,
	} = stylingOverrides;

	const { language }: ParamsProps = useParams();

	const [selected, setSelected] = useState(0);
	const [percentages, setPercentages] = useState<{ [key: number]: number }>({});
	const [totalAnswers, setTotalAnswers] = useState(0);
	const [isAnswered, setIsAnswered] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const eventId = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.event);
	const sessions = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.sessions);
	const channel_id = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.channel);
	const registrationId = useTypedSelector((state) => state.LiveEventReducer.registrationId);
	const registration_on = useTypedSelector((state) => state.LiveEventReducer.eventBundle?.registration_on);
	const workingSession = useTypedSelector((state) => state.CreateEventReducer.workingSession);
	const previewMode = useTypedSelector((state) => state.CreateEventReducer.previewMode);
	const token = useTypedSelector(state => state.LiveEventReducer.blProfileUserToken);
	const leaderboardEnabled = !!useTypedSelector(state => state.LiveEventReducer.leaderboardIsOn);

	const dispatch = useAppDispatch();

	const session = useMemo(() => {
		if (previewMode === 'session') return workingSession;

		return sessions?.find(session => session.uuid === sessionUuid);
	}, [previewMode, sessions, sessionUuid, workingSession]);

	const namespace = useTranslationRoute();
	const { t } = useTranslate([namespace, "session"]);
	const isSubmitDisabled = isAnswered || selected === 0 || (previewMode === 'session') || isSubmitting;

	// Get question stats (answer submission totals for each option)
	const getStatsResults = useCallback(async (selectedOption?: number) => {
		try {
			const level = session ? 'session' : 'event';
			const levelId = session ? session.session : eventId;
			if (!levelId) return;

			const stats = await GetSurveyQuestionResults(level, levelId, question.survey_question, question.question_type);

			// Since stats results are cached, this allows a fresh answer to be counted
			if (selectedOption) {
				stats.total += 1;
				stats.counts[selectedOption.toString()] += 1;
			}
			setPercentages(stats.counts);
			setTotalAnswers(stats.total);
		} catch (e) {
			console.error(e);
		}
	}, [eventId, question, session]);

	// On question load/next/prev
	useEffect(() => {
		if (setShowQuizCompleteScreen) {
			setShowQuizCompleteScreen(false);
		}
		if (!eventId) return;
		// TODO here surveys are by eventId, not session - update microservice query to ignore session?
		// if the user refreshes we want to keep the answers they submitted shown
		const lsKey = getPollAnswerLocalStorageKey(question, surveyId, eventId);
		const lsKeyBackwardsCompatible = getPollAnswerLocalStorageKeyBackwardsCompatible(question, surveyId);
		const answer: SurveyAnswerBody | null = getStorageObject(lsKey) ?? getStorageObject(lsKeyBackwardsCompatible);

		// If question has been answered, show answer selected, disable submit and get stats
		if (answer?.radio_answer) {
			setSelected(answer.radio_answer);
			setIsAnswered(true);
			if (show_results) {
				getStatsResults();
			}
		}
	}, [eventId, getStatsResults, question, show_results, surveyId]);

	// Handles selecting an option
	const handleChange = (value: string | number) => {
		setSelected(Number(value));
	};

	const handleSelectNext = () => {
		setCurrentQuestion(currentQuestion + 1);
		if (setShowQuizCompleteScreen) {
			setShowQuizCompleteScreen(true);
		}

	}

	const showNext = () => {
		return <button disabled={survey.survey_type !== SurveyType.quiz && currentQuestion + 1 === maxQuestions} onClick={() => handleSelectNext()}>{t("session:Next")}</button>
	}

	const showSubmit = () => {
		return (
			<button
				className={classNames('primary next', primaryButtonOverrides)} type="submit"
				onClick={() => handleSubmit(selected)}
				disabled={isSubmitDisabled}>
				{isSubmitting ? (
					<WaitingIndicator />
				) : t(`${namespace}:callToAction`, (content?.callToAction?.button_name?.[language] || t("session:Submit")))
				}
			</button>
		)
	}

	const displayButtonRow = () => {
		return (
			<div className='survey-pagination'>
				{!viewAll && <button disabled={currentQuestion === 0} onClick={() => setCurrentQuestion(currentQuestion - 1)}>{t("session:Previous")}</button>}
				<span>{currentQuestion + 1}/{maxQuestions}</span>
				{viewAll || selected === 0 || (!isSubmitDisabled && selected !== 0) ? (
					<>
						{showSubmit()}
					</>
				) : (
					<>
						{showNext()}
					</>
				)}
			</div >
		);
	};

	// Submits answer to question
	const handleSubmit = async (option: number) => {
		if (question.required && !selected) return;
		if (!module.id || !eventId) return;

		const body: SurveyAnswerBody = {
			session: session?.session,
			survey_question: question.survey_question,
			module: module.id,
			event: eventId,
			text_answer: null,
			radio_answer: selected,
			checkbox_answer: null,
			session_uuid: session?.uuid,
			language: language,
			channel: channel_id,
			leaderboard_enabled: leaderboardEnabled
		};
		if (registration_on && registrationId) {
			body.registration_id = registrationId;
		}

		try {
			setIsSubmitting(true);

			// Get stats first; the fresh answer will be tacked on simultaneously (prevents possible double count)
			if (show_results) {
				await getStatsResults(selected);
			}

			// Send the message to microservices
			await SubmitSurveyAnswer(body, token);

			//set in local storage in case they refresh
			const lsKey = getPollAnswerLocalStorageKey(question, surveyId, eventId);
			setStorageObject(lsKey, body, 48);

			// Check quiz for completion (checks all, but hopefully we have a few microseconds to spare)
			// Quizzes currently only have radios, so this is only found here and in survey.tsx
			if (survey.survey_type === SurveyType.quiz) {
				dispatch(checkQuizzesForCompletion(module, eventId, sessions));
			}
			setIsAnswered(true);
		} catch (err) {
			setPercentages({});
			setTotalAnswers(0);
			console.error(err);
		} finally {
			setIsSubmitting(false);
		}
	};

	// Calculates percentage for option results/stats bar
	const percentage = (option: SurveyQuestionOption) => {
		if (!totalAnswers) {
			return null;
		}
		if (percentages[option.survey_question_option]) {
			return Math.round(percentages[option.survey_question_option] / totalAnswers * 100);
		}

		return 0;
	};

	return (
		<div className="question-container">
			<TypographyItem className={classNames('question-title evt-heading-4', template)} tagName="p">
				{t(`${namespace}:survey.${surveyId}.questions.${question?.survey_question}`, (question?.title?.[language] as string ?? question?.title?.base))}
			</TypographyItem>
			{
				question.options.map(option => {
					return (
						<div
							className="survey-option-wrapper"
							key={`survey-option-${option.survey_question_option}`}
						>
							<SurveyOption
								value={option.survey_question_option}
								title={t(`${namespace}:survey.${surveyId}.questions.${question?.survey_question}.options.${option.survey_question_option}`, option.label?.[language] as string ?? option.label.base)}
								selected={selected === option.survey_question_option}
								onClick={handleChange}
								percentage={show_results ? percentage(option) : null}
								disabled={isAnswered}
								surveyType={survey.survey_type}
								isCorrect={option.is_correct ? option.is_correct : false}
							/>
						</div>
					);
				})
			}
			{displayButtonRow()}
		</div >
	);
};

export default RadioSurveyQuestion;
